import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Alert,
  ActivityIndicator,
  Image,
  Linking,
  Platform
} from 'react-native';
import { observer } from '@legendapp/state/react';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { SettingsAccountLogger } from '../../../../utils/logger';
import theme from '../../../../config/theme';
import { globalState, userActions } from '../../../../state/globalState';
import { observable } from '@legendapp/state';
import { updateUserProfile, uploadUserProfileImage, getUserProfile, deleteUserProfileImage } from '../../../../services/userService';
import { profileScreenViewModel } from '../../../../state/computedStates';
import * as ImagePicker from 'expo-image-picker';
import { databaseService } from '../../../../services/databaseService';
import { APP_CONSTANTS } from '../../../../config/constants';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { useWebAlert } from '../../../../components/WebAlert';
import * as FileSystem from 'expo-file-system/legacy';
import * as ImageManipulator from 'expo-image-manipulator';
import { v4 as uuidv4 } from 'uuid';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
//import { debugPause } from '../../../../utils/debugPause';
import { pickImageFromLibrary, takePhotoFromCamera } from '@/utils/media/imagePicker';
import {
  getActionSheetContainerPaddingBottom,
  getSettingsContentPaddingBottom,
  getSettingsHeaderPaddingTop,
} from '@/utils/safeArea';
import { supabase } from '../../../../services/supabase';
import { logout } from '../../../../services/authService';
import { captureSentryExceptionWithScope } from '@/services/monitoring/sentry';
import { isRecoveryRuntimeBlocked } from '@/services/recovery/RecoveryRuntimeGate';
import { LEGAL_URLS } from '@/config/legal';

// Logger para a página de configurações de conta
const logger = SettingsAccountLogger.Main;

// Estado local da UI para esta página
export const accountUIState = observable({
  isLoading: false,
  isSaving: false,
  editField: null as string | null,
  editData: {
    name: '',
    telefone: ''
  }
});

// Ações para manipular o estado da UI
export const accountUIActions = {
  setLoading: (v: boolean) => accountUIState.isLoading.set(v),
  setSaving: (v: boolean) => accountUIState.isSaving.set(v),
  setEditField: (field: string | null) => accountUIState.editField.set(field),
  updateEditData: (data: any) => {
    accountUIState.editData.set({
      ...accountUIState.editData.get(),
      ...data
    });
  },
  initFromProfile: (profile: any) => {
    if (profile) {
      accountUIState.editData.set({
        name: profile.name || '',
        telefone: profile.telefone || ''
      });
    }
  }
};

// Interface para o componente de campo editável
interface EditableFieldProps {
  label: string;
  value: string;
  fieldName: string;
  editable?: boolean;
  isEditing: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onChangeText: (text: string) => void;
  placeholder?: string;
  helpText?: string;
  keyboardType?: any;
}

// Componente de campo editável
const EditableField = observer(({
  label,
  value,
  fieldName,
  editable = true,
  isEditing,
  onEdit,
  onSave,
  onCancel,
  onChangeText,
  placeholder,
  helpText,
  keyboardType = 'default'
}: EditableFieldProps) => {
  const currentEditField = accountUIState.editField.get();
  const isCurrentlyEditing = currentEditField === fieldName;
  const editData = accountUIState.editData.get();
  const editValue = editData[fieldName as keyof typeof editData];
  const isSaving = accountUIState.isSaving.get();

  // Log para depuração
  logger.info(`[EditableField] ${fieldName} - currentEditField: ${currentEditField}, isCurrentlyEditing: ${isCurrentlyEditing}, isEditing prop: ${isEditing}`);

  return (
    <View style={styles.fieldRow}>
      <View style={styles.fieldLabelContainer}>
        <Text style={styles.fieldLabel}>{label}</Text>
      </View>

      <View style={styles.fieldValueContainer}>
        {isCurrentlyEditing ? (
          <View style={styles.editContainer}>
            <TextInput
              style={styles.input}
              value={editValue as string}
              onChangeText={onChangeText}
              placeholder={placeholder}
              placeholderTextColor={theme.colors.text.tertiary}
              keyboardType={keyboardType}
              autoFocus
            />
            <View style={styles.editActions}>
              <TouchableOpacity
                style={[styles.editButton, styles.cancelButton]}
                onPress={onCancel}
                disabled={isSaving}
              >
                <Text style={styles.editButtonText}>Cancelar</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.editButton}
                onPress={onSave}
                disabled={isSaving}
              >
                {isSaving ? (
                  <ActivityIndicator size="small" color={theme.colors.text.inverse} />
                ) : (
                  <Text style={styles.editButtonText}>Salvar</Text>
                )}
              </TouchableOpacity>
            </View>

            {helpText && (
              <Text style={styles.helpText}>{helpText}</Text>
            )}
          </View>
        ) : (
          <View style={styles.displayContainer}>
            <Text style={styles.fieldValue}>{value || 'Não informado'}</Text>

            {editable && (
              <TouchableOpacity
                style={styles.editIconButton}
                onPress={() => {
                  logger.info(`[EditButton] Botão de edição para ${fieldName} clicado`);
                  onEdit();
                }}
              >
                <MaterialCommunityIcons name="pencil" size={24} color={theme.colors.primary.default} />
              </TouchableOpacity>
            )}
          </View>
        )}
      </View>
    </View>
  );
});

// Componente de campo de avatar/foto
const AvatarField = observer(() => {
  const profileView = profileScreenViewModel.get();
  const [uploading, setUploading] = useState(false);
  const [previewUri, setPreviewUri] = useState<string | null>(null);
  const { showActionSheetWithOptions } = useActionSheet();
  const { showAlert } = useWebAlert();
  const safeAreaInsets = useSafeAreaInsets();
  
  // Função para mostrar alerta adaptável à plataforma
  const showMessage = (title: string, msg: string, onOk?: () => void) => {
    if (Platform.OS !== 'android') {
      // No web e no iOS, usar o alerta temático do app
      showAlert(title, msg, [{ text: 'OK', onPress: onOk }]);
    } else {
      // No Android, manter o Alert nativo
      Alert.alert(title, msg, [{ text: 'OK', onPress: onOk }]);
    }
  };

  // O photoUrl já contém o fallback apropriado (photo_url || photo_local_path || null)
  // no profileScreenViewModel

  // Usar o previewUri se disponível, caso contrário usar o photoUrl do perfil
  const uriToShow = previewUri ?? (profileView?.personalInfo.photoUrl || null);

  const handlePickImage = () => {
    showActionSheetWithOptions(
      {
        options: ['Cancelar', 'Tirar foto', 'Galeria'],
        cancelButtonIndex: 0,
        title: 'Selecionar imagem',
        message: 'De onde você quer obter sua foto de perfil?',
        // Estilos baseados no tema da aplicação
        titleTextStyle: {
          color: theme.colors.text.primary,
          fontSize: theme.fontSizes.md,
          fontWeight: theme.fontWeights.medium as any,
        },
        messageTextStyle: {
          color: theme.colors.text.secondary,
          fontSize: theme.fontSizes.sm,
        },
        containerStyle: {
          backgroundColor: theme.colors.background.primary,
          paddingBottom: getActionSheetContainerPaddingBottom(safeAreaInsets.bottom),
        },
        textStyle: {
          color: theme.colors.primary.default,
          fontSize: theme.fontSizes.md,
        },
      },
      async (buttonIndex) => {
        if (buttonIndex === 1) {
          // câmera
          await takePhoto();
        }
        if (buttonIndex === 2) {
          // galeria
          await pickFromLibrary();
        }
      }
    );
  };

  const takePhoto = async () => {
    try {
      const outcome = await takePhotoFromCamera({
        mediaTypes: ['images'],
        allowsEditing: false,
        aspect: [1, 1],
        quality: 0.7,
      }, {
        source: 'settings_account_profile_photo',
        screen: 'settings/account',
        action: 'take_profile_photo_with_camera',
        entityType: 'user_profile_photo',
        entityId: globalState.user.user.get()?.id ?? null,
      });

      if (outcome.status === 'denied') {
        showMessage('Permissão negada', 'Precisamos de permissão para acessar a câmera.');
        return;
      }

      if (outcome.status === 'failed') {
        logger.error('Erro ao tirar foto:', outcome.error);
        showMessage('Erro', 'Não foi possível tirar a foto.');
        return;
      }

      if (outcome.status === 'success' && outcome.result.assets?.length > 0) {
        const selectedImage = outcome.result.assets[0];
        await handleSelectedImage(selectedImage);
      }
    } catch (error) {
      logger.error('Erro ao tirar foto:', error);
      showMessage('Erro', 'Não foi possível tirar a foto.');
    }
  };

  const pickFromLibrary = async () => {
    try {
      const outcome = await pickImageFromLibrary({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: false,
        aspect: [1, 1],
        quality: 0.7,
      }, {
        source: 'settings_account_profile_photo',
        screen: 'settings/account',
        action: 'pick_profile_photo_from_library',
        entityType: 'user_profile_photo',
        entityId: globalState.user.user.get()?.id ?? null,
      });

      if (outcome.status === 'denied') {
        showMessage('Permissão negada', 'Precisamos de permissão para acessar suas fotos.');
        return;
      }

      if (outcome.status === 'failed') {
        logger.error('Erro ao selecionar imagem da galeria:', outcome.error);
        showMessage('Erro', 'Não foi possível selecionar a imagem.');
        return;
      }

      if (outcome.status !== 'success' || !outcome.result.assets?.length) {
        return;
      }

      const selectedImage = outcome.result.assets[0];
      await handleSelectedImage(selectedImage);
    } catch (error) {
      logger.error('Erro ao selecionar imagem da galeria:', error);
      showMessage('Erro', 'Não foi possível selecionar a imagem.');
    }
  };

  const handleSelectedImage = async (asset: ImagePicker.ImagePickerAsset) => {
    if (!globalState.user.user.get()?.id) return;
    const userId = globalState.user.user.get()!.id;

    setUploading(true);

    logger.info(`[handleSelectedImage] Iniciando processamento para asset.uri=${asset.uri}`);

    try {
      let localUri = asset.uri;

      // --- WEB FLOW -----------------------------------------
      if (Platform.OS === 'web') {
        const base64 = await compressImageWeb(asset.uri);
        localUri = `data:image/jpeg;base64,${base64}`;
        logger.info(`[handleSelectedImage][WEB] Imagem comprimida, tamanho ${(base64.length / 1024).toFixed(1)}KB`);
      }

      // --- MOBILE FLOW --------------------------------------
      if (Platform.OS !== 'web') {
        // Compress using expo-image-manipulator
        const manipulated = await ((ImageManipulator as any).manipulate ?? ImageManipulator.manipulateAsync)(
          asset.uri,
          [{ resize: { width: 800 } }],
          { compress: 0.75, format: ImageManipulator.SaveFormat.JPEG, base64: true }
        );

        const base64Data = manipulated.base64 || '';
        const fileName = `${userId}-${Date.now()}-${uuidv4()}.jpg`;
        const destPath = `user-photos/${fileName}`;
        const fileUri = `${FileSystem.documentDirectory}${destPath}`;

        // Ensure directory exists
        const dir = `${FileSystem.documentDirectory}user-photos/`;
        const dirInfo = await FileSystem.getInfoAsync(dir);
        if (!dirInfo.exists) {
          await FileSystem.makeDirectoryAsync(dir, { intermediates: true });
        }

        await FileSystem.writeAsStringAsync(fileUri, base64Data, { encoding: FileSystem.EncodingType.Base64 });
        logger.info(`[handleSelectedImage][MOBILE] Arquivo salvo em ${fileUri}`);

        // Remove temporary original file
        try { await FileSystem.deleteAsync(asset.uri, { idempotent: true }); } catch {}

        localUri = fileUri;
      }

      // Preview while uploading
      setPreviewUri(localUri);

      logger.info(`[handleSelectedImage] Chamando uploadUserProfileImage com localUri=${localUri}`);
      const result = await uploadUserProfileImage(userId, localUri);
      if (!result.success) throw new Error(result.error);

      await getUserProfile(userId);
      showMessage('Sucesso', 'Foto atualizada com sucesso!');
    } catch (err) {
      logger.warn('Falha no upload:', err);
      showMessage('Aviso', 'Imagem salva localmente e será enviada quando estiver online.');
    } finally {
      setUploading(false);
      setPreviewUri(null);
    }
  };

  // Função para remover a foto de perfil
  const handleRemovePhoto = async () => {
    if (!globalState.user.user.get()?.id) return;
    const userId = globalState.user.user.get()!.id;
    setUploading(true);
    try {
      logger.info('Chamando deleteUserProfileImage para userId: ' + userId);
      const result = await deleteUserProfileImage(userId);
      if (!result.success) {
        throw new Error(result.error);
      }
      logger.info('deleteUserProfileImage retornou sucesso');
      await getUserProfile(userId);
      showMessage('Sucesso', 'Foto removida com sucesso!');
    } catch (err) {
      logger.error('Falha ao remover foto:', err);
      showMessage('Aviso', 'Falha ao remover a foto. Tente novamente.');
    } finally {
      setUploading(false);
      setPreviewUri(null);
    }
  };

  if (!profileView) return null;

  return (
    <View style={styles.fieldRow}>
      <View style={styles.fieldLabelContainer}>
        <Text style={styles.fieldLabel}>Foto</Text>
      </View>
      <View style={styles.fieldValueContainer}>
        <View style={{ width: '100%' }}>
          {/* Container da imagem */}
          <View style={{ alignItems: 'flex-start' }}>
            {uriToShow ? (
              <Image
                source={{ uri: uriToShow }}
                style={styles.avatar}
              />
            ) : (
              <View style={[styles.avatar, styles.avatarPlaceholder]}>
                <MaterialCommunityIcons name="account" size={40} color={theme.colors.text.inverse} />
              </View>
            )}
          </View>
          {/* Linha de botões abaixo da foto */}
          <View style={{ flexDirection: 'row', marginTop: theme.spacing.sm }}>
            <TouchableOpacity
              style={styles.changePhotoButton}
              onPress={handlePickImage}
              disabled={uploading}
            >
              {uploading ? (
                <ActivityIndicator size="small" color={theme.colors.primary.default} />
              ) : (
                <Text style={styles.changePhotoText}>Alterar foto</Text>
              )}
            </TouchableOpacity>
            {/* Botão de remover foto */}
            {uriToShow && (
              <TouchableOpacity
                style={[styles.changePhotoButton, { marginLeft: theme.spacing.sm, borderColor: theme.colors.util.error }]}
                onPress={handleRemovePhoto}
                disabled={uploading}
              >
                {uploading ? (
                  <ActivityIndicator size="small" color={theme.colors.util.error} />
                ) : (
                  <Text style={[styles.changePhotoText, { color: theme.colors.util.error }]}>Remover foto</Text>
                )}
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    </View>
  );
});

// ------------------------------------------------------------
// Helper (web-only) – compresses and resizes an image picked from
// the gallery before it is uploaded. Mirrors the behaviour
// implemented in ChecklistPhotoField.tsx.
// ------------------------------------------------------------
async function compressImageWeb(
  uri: string,
  options: { maxWidth?: number; quality?: number } = {}
): Promise<string> {
  const { maxWidth = 1280, quality = 0.75 } = options;

  logger.info(`[compressImageWeb] Iniciando compressão para uri=${uri}`);
  const startTs = Date.now();

  return new Promise((resolve, reject) => {
    const img: HTMLImageElement = document.createElement('img');
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      logger.info(`[compressImageWeb] Imagem carregada (${img.width}x${img.height})`);
      let { width, height } = img;
      if (width > maxWidth) {
        const ratio = maxWidth / width;
        width = maxWidth;
        height = height * ratio;
        logger.info(`[compressImageWeb] Redimensionando para ${width}x${height}`);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas not supported'));
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);

      const dataUrl = canvas.toDataURL('image/jpeg', quality);
      const duration = Date.now() - startTs;
      logger.info(`[compressImageWeb] Concluído em ${duration}ms, tamanho ${(dataUrl.length / 1024).toFixed(1)}KB`);
      resolve(dataUrl.split(',')[1]);
    };

    img.onerror = (err) => {
      logger.error('[compressImageWeb] Erro ao carregar imagem', err);
      reject(err instanceof Error ? err : new Error('Image load error'));
    };

    img.src = uri;
  });
}

// Página principal de configurações de conta
const AccountScreen = observer(() => {
  const router = useRouter();
  const { section } = useLocalSearchParams<{ section?: string }>();
  const safeAreaInsets = useSafeAreaInsets();
  const headerPaddingTop = getSettingsHeaderPaddingTop(safeAreaInsets.top);
  const scrollViewBottomPadding = getSettingsContentPaddingBottom(safeAreaInsets.bottom);
  const showDebugActions = APP_CONSTANTS.FEATURE_FLAGS.SHOW_DEBUG_ACTIONS;
  const profileView = profileScreenViewModel.get();
  const rawProfile = globalState.user.profile.get();
  const { showAlert } = useWebAlert();
  const isIOSAccountScreen = Platform.OS === 'ios';
  const isPrivacyAndDeletionView = section === 'delete';
  const [isDeleteConfirmVisible, setIsDeleteConfirmVisible] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  
  // Função para mostrar alerta adaptável à plataforma
  const showMessage = (title: string, msg: string, onOk?: () => void) => {
    if (Platform.OS !== 'android') {
      // No web e no iOS, usar o alerta temático do app
      showAlert(title, msg, [{ text: 'OK', onPress: onOk }]);
    } else {
      // No Android, manter o Alert nativo
      Alert.alert(title, msg, [{ text: 'OK', onPress: onOk }]);
    }
  };

  useEffect(() => {
    logger.debug('Página de configurações de conta inicializada');
    logger.info('[AccountScreen] useEffect - profileView disponível:', !!profileView);

    // Inicializar dados de edição a partir do perfil
    if (profileView) {
      accountUIActions.initFromProfile(profileView.personalInfo);
      logger.info('[AccountScreen] editData inicializado:', accountUIState.editData.get());
    }

    return () => {
      logger.debug('Página de configurações de conta desmontada');
      // Limpar estado de edição ao desmontar
      accountUIActions.setEditField(null);
    };
  }, [profileView]);

  // Funções para lidar com a edição de campos
  const handleEditField = (fieldName: string) => {
    logger.info(`[handleEditField] Iniciando edição do campo: ${fieldName}, valor anterior: ${accountUIState.editField.get()}`);
    accountUIActions.setEditField(fieldName);
    logger.info(`[handleEditField] Valor após setEditField: ${accountUIState.editField.get()}`);
  };

  const handleCancelEdit = () => {
    logger.info('[handleCancelEdit] Cancelando edição');
    accountUIActions.setEditField(null);
    // Restaurar valores originais
    if (profileView) {
      accountUIActions.initFromProfile(profileView.personalInfo);
    }
  };

  const handleSaveField = async () => {
    try {
      const editField = accountUIState.editField.get();
      const editData = accountUIState.editData.get();
      const userId = globalState.user.user.get()?.id;

      if (!userId || !editField) {
        showMessage('Erro', 'Não foi possível identificar os dados a serem atualizados.');
        return;
      }

      accountUIActions.setSaving(true);

      // Objeto com apenas o campo que está sendo editado
      const updateData: any = {};
      updateData[editField] = editData[editField as keyof typeof editData];

      // Seguindo o padrão offline-first:
      // 1. Atualizar no banco de dados local primeiro usando databaseService
      // 2. A atualização local registrará a operação como pendente para sincronização automática
      try {
        // Atualizar no banco local - isso registra automaticamente como pendente para sincronização
        const success = await databaseService.updateItem(
          APP_CONSTANTS.DATABASE.TABLES.USERS.NAME,
          userId,
          updateData
        );

        if (!success) {
          throw new Error('Falha ao atualizar dados localmente');
        }

        logger.info(`Campo ${editField} atualizado com sucesso localmente`);

        // Atualizar o estado global para refletir as mudanças imediatamente
        const currentProfile = globalState.user.profile.get();
        if (currentProfile) {
          userActions.setProfile({
            ...currentProfile,
            ...updateData
          });
          logger.info(`Estado global do perfil atualizado com ${editField}=${updateData[editField]}`);
        }

        // Atualização local feita com sucesso
        showMessage('Sucesso', 'Dados atualizados com sucesso!');
        accountUIActions.setEditField(null);
        accountUIActions.setSaving(false);

        // A sincronização com o servidor será feita automaticamente pelo SyncService
        // quando houver conexão disponível, não é necessário chamar updateUserProfile
      } catch (localError) {
        logger.error('Erro ao atualizar localmente:', localError);

        // Se falhar na atualização local, tenta diretamente pelo serviço (abordagem tradicional)
        logger.info('Tentando atualização via updateUserProfile após falha local');
        const { success, error } = await updateUserProfile(userId, updateData);

        if (error) {
          logger.error('Erro ao atualizar perfil via serviço:', error);
          showMessage('Erro', 'Não foi possível atualizar seus dados.');
          accountUIActions.setSaving(false);
          return;
        }

        if (success) {
          logger.info(`Campo ${editField} atualizado com sucesso via serviço`);
          showMessage('Sucesso', 'Dados atualizados com sucesso!');
          accountUIActions.setEditField(null);
        }
      }

      accountUIActions.setSaving(false);
    } catch (error) {
      logger.error('Exceção ao atualizar perfil:', error);
      showMessage('Erro', 'Ocorreu um erro ao atualizar seus dados.');
      accountUIActions.setSaving(false);
    }
  };

  const handleOpenAppleSubscriptions = async () => {
    try {
      await Linking.openURL('https://apps.apple.com/account/subscriptions');
    } catch (error) {
      logger.warn('Não foi possível abrir a gestão de assinaturas da Apple:', error);
      showMessage('Aviso', 'Não foi possível abrir a página de assinaturas da Apple.');
    }
  };

  const handleOpenLegalUrl = async (url: string) => {
    try {
      await Linking.openURL(url);
    } catch (error) {
      logger.warn('Não foi possível abrir o documento legal:', error);
      showMessage('Aviso', 'Não foi possível abrir este documento agora.');
    }
  };

  const handleDeleteOwnAccount = async () => {
    if (isRecoveryRuntimeBlocked()) {
      showMessage(
        'Preservação em andamento',
        'A exclusão da conta fica indisponível até a preservação segura dos dados locais terminar.'
      );
      return;
    }

    if (deleteConfirmText.trim().toUpperCase() !== 'EXCLUIR') {
      showMessage('Confirmação necessária', 'Digite EXCLUIR para confirmar a exclusão da conta.');
      return;
    }

    setIsDeletingAccount(true);
    try {
      logger.warn('[deleteOwnAccount] Iniciando exclusão da própria conta');
      const { data, error } = await supabase.functions.invoke('deleteOwnAccount', {
        body: { confirmation: 'EXCLUIR' },
      });

      if (error) {
        logger.error('[deleteOwnAccount] Erro retornado pela Edge Function:', error);
        throw error;
      }

      logger.info('[deleteOwnAccount] Conta removida no servidor', data);
      await databaseService.clearAllData();
      await logout();
      userActions.clearUser();
      setIsDeleteConfirmVisible(false);
      setDeleteConfirmText('');
      router.replace('/(auth)/login');
    } catch (error) {
      logger.error('[deleteOwnAccount] Falha ao excluir conta:', error);
      captureSentryExceptionWithScope(error, {
        level: 'error',
        context: {
          flow: 'delete_own_account',
          stage: 'client_invoke',
          platform: Platform.OS,
        },
        tags: {
          flow: 'delete_own_account',
          stage: 'client_invoke',
          platform: Platform.OS,
        },
        fingerprint: ['delete_own_account', 'client_invoke', Platform.OS],
        dedupeKey: `delete_own_account:client_invoke:${Platform.OS}`,
        dedupeWindowMs: 30000,
      });
      showMessage('Erro', 'Não foi possível excluir sua conta agora. Tente novamente em instantes.');
    } finally {
      setIsDeletingAccount(false);
    }
  };

  // Navegação para voltar
  const handleGoBack = () => {
    router.back();
  };

  const renderLegalSection = () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Documentos legais</Text>
      <Text style={styles.legalDescription}>
        Consulte os documentos que explicam as condições de uso e como tratamos seus dados.
      </Text>
      <TouchableOpacity
        style={styles.legalLinkButton}
        onPress={() => handleOpenLegalUrl(LEGAL_URLS.termsOfUse)}
      >
        <MaterialCommunityIcons name="file-document-outline" size={20} color={theme.colors.primary.default} />
        <Text style={styles.legalLinkText}>Termos de Uso (EULA)</Text>
        <MaterialCommunityIcons name="open-in-new" size={18} color={theme.colors.primary.default} />
      </TouchableOpacity>
      <TouchableOpacity
        style={styles.legalLinkButton}
        onPress={() => handleOpenLegalUrl(LEGAL_URLS.privacyPolicy)}
      >
        <MaterialCommunityIcons name="shield-lock-outline" size={20} color={theme.colors.primary.default} />
        <Text style={styles.legalLinkText}>Política de Privacidade</Text>
        <MaterialCommunityIcons name="open-in-new" size={18} color={theme.colors.primary.default} />
      </TouchableOpacity>
    </View>
  );

  const renderDeleteAccountSection = () => (
    <View style={[styles.section, styles.dangerSection]}>
      <Text style={styles.sectionTitle}>Excluir conta</Text>
      <Text style={styles.dangerText}>
        A exclusão é definitiva e remove seu usuário, acesso de login, dados pessoais e foto de perfil. Dados operacionais da empresa já emitidos podem ser mantidos somente quando necessários para continuidade do serviço ou obrigações legais.
      </Text>
      {isIOSAccountScreen && (
        <Text style={styles.dangerText}>
          Importante: excluir a conta não cancela uma assinatura da App Store. A cobrança continua pela Apple até ser cancelada. Você pode abrir a gestão de assinaturas antes de continuar.
        </Text>
      )}

      {isDeleteConfirmVisible ? (
        <View style={styles.deleteConfirmBox}>
          <Text style={styles.deleteConfirmationLabel}>
            Para confirmar a exclusão definitiva, digite EXCLUIR:
          </Text>
          <TextInput
            value={deleteConfirmText}
            onChangeText={setDeleteConfirmText}
            placeholder="Digite EXCLUIR"
            placeholderTextColor={theme.colors.text.tertiary}
            autoCapitalize="characters"
            style={styles.deleteInput}
            editable={!isDeletingAccount}
          />
          <View style={styles.deleteActions}>
            {isIOSAccountScreen && (
              <TouchableOpacity
                style={[styles.deleteSecondaryButton, isDeletingAccount && styles.disabledButton]}
                onPress={handleOpenAppleSubscriptions}
                disabled={isDeletingAccount}
              >
                <Text style={styles.deleteSecondaryText}>Gerenciar assinatura Apple</Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity
              style={[styles.deleteSecondaryButton, isDeletingAccount && styles.disabledButton]}
              onPress={() => {
                setIsDeleteConfirmVisible(false);
                setDeleteConfirmText('');
              }}
              disabled={isDeletingAccount}
            >
              <Text style={styles.deleteSecondaryText}>Cancelar</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.deleteButton,
                (isDeletingAccount || deleteConfirmText.trim().toUpperCase() !== 'EXCLUIR') && styles.disabledButton,
              ]}
              onPress={handleDeleteOwnAccount}
              disabled={isDeletingAccount || deleteConfirmText.trim().toUpperCase() !== 'EXCLUIR'}
            >
              {isDeletingAccount ? (
                <ActivityIndicator size="small" color={theme.colors.text.inverse} />
              ) : (
                <Text style={styles.deleteButtonText}>Excluir conta definitivamente</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      ) : (
        <TouchableOpacity
          style={styles.deleteOutlineButton}
          onPress={() => setIsDeleteConfirmVisible(true)}
        >
          <MaterialCommunityIcons name="delete-outline" size={20} color={theme.colors.util.error} />
          <Text style={styles.deleteOutlineText}>Excluir minha conta</Text>
        </TouchableOpacity>
      )}
    </View>
  );

  if (!profileView) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary.default} />
        <Text style={styles.loadingText}>Carregando informações...</Text>
      </View>
    );
  }

  if (isPrivacyAndDeletionView) {
    return (
      <View style={styles.container}>
        <View style={[styles.header, { paddingTop: headerPaddingTop }]}>
          <TouchableOpacity style={styles.backButton} onPress={handleGoBack}>
            <MaterialCommunityIcons name="arrow-left" size={24} color={theme.colors.text.inverse} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Privacidade e conta</Text>
          <View style={styles.headerRight} />
        </View>
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={[styles.scrollViewContent, { paddingBottom: scrollViewBottomPadding }]}
          showsVerticalScrollIndicator={false}
        >
          {renderLegalSection()}
          {renderDeleteAccountSection()}
        </ScrollView>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={[styles.header, { paddingTop: headerPaddingTop }]}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={handleGoBack}
        >
          <MaterialCommunityIcons name="arrow-left" size={24} color={theme.colors.text.inverse} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Conta</Text>
        <View style={styles.headerRight} />
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[styles.scrollViewContent, { paddingBottom: scrollViewBottomPadding }]}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.section}>
          <AvatarField />

          <EditableField
            label="Nome"
            value={profileView.personalInfo.name}
            fieldName="name"
            isEditing={true}
            onEdit={() => handleEditField('name')}
            onSave={handleSaveField}
            onCancel={handleCancelEdit}
            onChangeText={(text) => accountUIActions.updateEditData({ name: text })}
            placeholder="Seu nome completo"
          />

          <EditableField
            label="E-mail"
            value={profileView.personalInfo.email}
            fieldName="email"
            editable={false}
            isEditing={false}
            onEdit={() => { }}
            onSave={() => { }}
            onCancel={() => { }}
            onChangeText={() => { }}
            helpText="O e-mail não pode ser alterado diretamente"
          />

          <EditableField
            label="Telefone"
            value={profileView.personalInfo.telefone}
            fieldName="telefone"
            isEditing={true}
            onEdit={() => handleEditField('telefone')}
            onSave={handleSaveField}
            onCancel={handleCancelEdit}
            onChangeText={(text) => accountUIActions.updateEditData({ telefone: text })}
            placeholder="Seu telefone de contato"
            keyboardType="phone-pad"
          />
        </View>

        {renderDeleteAccountSection()}

        {showDebugActions && (
          <>
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Profile ViewModel Debug</Text>
              {profileView && Object.entries(profileView).map(([group, values]) => (
                <View key={group} style={styles.debugSection}>
                  <Text style={styles.debugSectionTitle}>{group}</Text>
                  {Object.entries(values).map(([key, value]) => (
                    <View key={key} style={styles.debugRow}>
                      <Text style={styles.debugKey}>{key}</Text>
                      <Text style={styles.debugValue}>{String(value)}</Text>
                    </View>
                  ))}
                </View>
              ))}
            </View>
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>GlobalState User Profile Debug</Text>
              {rawProfile && Object.entries(rawProfile).map(([key, value]) => (
                <View key={key} style={styles.debugRow}>
                  <Text style={styles.debugKey}>{key}</Text>
                  <Text style={styles.debugValue}>{String(value)}</Text>
                </View>
              ))}
            </View>
          </>
        )}
      </ScrollView>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.secondary,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background.secondary,
  },
  loadingText: {
    marginTop: theme.spacing.md,
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.secondary,
  },
  header: {
    backgroundColor: theme.colors.primary.default,
    padding: theme.spacing.lg,
    paddingTop: Platform.OS === 'ios' ? theme.spacing.xl + theme.spacing.sm : theme.spacing.lg,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: theme.fontSizes.xl,
    fontWeight: theme.fontWeights.bold as any,
    color: theme.colors.text.inverse,
  },
  headerRight: {
    width: 40,
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    paddingBottom: theme.spacing.xl,
  },
  section: {
    backgroundColor: theme.colors.background.primary,
    margin: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    ...theme.shadows.md,
    overflow: 'hidden',
  },
  dangerSection: {
    paddingVertical: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.util.error,
  },
  dangerText: {
    fontSize: theme.fontSizes.sm,
    lineHeight: 20,
    color: theme.colors.text.secondary,
    marginHorizontal: theme.spacing.lg,
    marginBottom: theme.spacing.sm,
  },
  legalDescription: {
    fontSize: theme.fontSizes.sm,
    lineHeight: 20,
    color: theme.colors.text.secondary,
    marginHorizontal: theme.spacing.lg,
    marginBottom: theme.spacing.sm,
  },
  legalLinkButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
    minHeight: 48,
    paddingHorizontal: theme.spacing.lg,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border.light,
  },
  legalLinkText: {
    flex: 1,
    color: theme.colors.primary.default,
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.medium as any,
  },
  deleteOutlineButton: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: theme.spacing.xs,
    borderWidth: 1,
    borderColor: theme.colors.util.error,
    borderRadius: theme.borderRadius.sm,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    marginHorizontal: theme.spacing.lg,
    marginTop: theme.spacing.xs,
  },
  deleteOutlineText: {
    color: theme.colors.util.error,
    fontSize: theme.fontSizes.sm,
    fontWeight: theme.fontWeights.medium as any,
  },
  deleteConfirmBox: {
    marginHorizontal: theme.spacing.lg,
    marginTop: theme.spacing.sm,
  },
  deleteInput: {
    borderWidth: 1,
    borderColor: theme.colors.util.error,
    borderRadius: theme.borderRadius.sm,
    padding: theme.spacing.sm,
    color: theme.colors.text.primary,
    fontSize: theme.fontSizes.md,
    marginBottom: theme.spacing.sm,
  },
  deleteConfirmationLabel: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.primary,
    marginBottom: theme.spacing.xs,
  },
  deleteActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing.sm,
  },
  deleteButton: {
    backgroundColor: theme.colors.util.error,
    borderRadius: theme.borderRadius.sm,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 40,
  },
  deleteButtonText: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.sm,
    fontWeight: theme.fontWeights.medium as any,
  },
  deleteSecondaryButton: {
    borderWidth: 1,
    borderColor: theme.colors.border.default,
    borderRadius: theme.borderRadius.sm,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    minHeight: 40,
    justifyContent: 'center',
  },
  deleteSecondaryText: {
    color: theme.colors.text.secondary,
    fontSize: theme.fontSizes.sm,
    fontWeight: theme.fontWeights.medium as any,
  },
  disabledButton: {
    opacity: 0.5,
  },
  fieldRow: {
    flexDirection: 'row',
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border.light,
  },
  fieldLabelContainer: {
    width: 100,
    justifyContent: 'center',
  },
  fieldLabel: {
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.medium as any,
    color: theme.colors.text.secondary,
  },
  fieldValueContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  displayContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  fieldValue: {
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.primary,
    flex: 1,
  },
  editIconButton: {
    padding: theme.spacing.xs,
  },
  editContainer: {
    width: '100%',
  },
  input: {
    borderWidth: 1,
    borderColor: theme.colors.border.default,
    borderRadius: theme.borderRadius.sm,
    padding: theme.spacing.sm,
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.primary,
    marginBottom: theme.spacing.sm,
  },
  editActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  editButton: {
    backgroundColor: theme.colors.primary.default,
    borderRadius: theme.borderRadius.sm,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    marginLeft: theme.spacing.sm,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelButton: {
    backgroundColor: theme.colors.text.tertiary,
  },
  editButtonText: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.sm,
    fontWeight: theme.fontWeights.medium as any,
  },
  helpText: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.tertiary,
    marginTop: theme.spacing.xs,
    fontStyle: 'italic',
  },
  avatarContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  avatar: {
    width: 70,
    height: 70,
    borderRadius: 35,
  },
  avatarPlaceholder: {
    backgroundColor: theme.colors.primary.light,
    justifyContent: 'center',
    alignItems: 'center',
  },
  changePhotoButton: {
    borderWidth: 1,
    borderColor: theme.colors.primary.default,
    borderRadius: theme.borderRadius.sm,
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: theme.spacing.xs,
  },
  changePhotoText: {
    color: theme.colors.primary.default,
    fontSize: theme.fontSizes.xs,
    fontWeight: theme.fontWeights.medium as any,
  },
  sectionTitle: {
    fontSize: theme.fontSizes.lg,
    fontWeight: theme.fontWeights.bold as any,
    color: theme.colors.text.primary,
    marginBottom: theme.spacing.sm,
    marginHorizontal: theme.spacing.lg,
  },
  debugSection: {
    marginBottom: theme.spacing.md,
  },
  debugSectionTitle: {
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.medium as any,
    color: theme.colors.text.secondary,
    marginBottom: theme.spacing.xs,
    marginHorizontal: theme.spacing.lg,
  },
  debugRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: theme.spacing.xs,
    paddingHorizontal: theme.spacing.lg,
  },
  debugKey: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.tertiary,
  },
  debugValue: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.primary,
  },
});

export default AccountScreen; 
